import { useCallback, useEffect, useMemo, useState } from 'react';
import { faker } from '@faker-js/faker';
import image from 'assets/images/empty.svg';
import { addDays, format } from 'date-fns';
import * as Tabs from '@radix-ui/react-tabs';
import {
	VictoryAxis,
	VictoryBar,
	VictoryChart,
	VictoryLabel,
	VictoryLine,
	VictoryPie,
	VictoryTooltip,
	VictoryZoomContainer,
} from 'victory';
import classNames from 'classnames';
import { useSearchParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import {
	border,
	checkLicenseBits,
	determineBarWidth,
	determineDomainPadding,
	dynamicFontSizeStyles,
	fontFamily,
	formatSecondsToTime,
	intersects,
	userIs,
} from 'utils';
import { Facility, FacilityLicense, UserRole } from 'models';
import { ArtificialFacilityConfiguration, FacilityNurseStaffingAccordionItem } from 'pages/Settings/Tabs';
import { useAppSelector, useGetDefaultStartEndDateQuery, useGetSystemsQuery, useSystem } from 'store';
import {
	Accordion,
	Button,
	ButtonStack,
	DataTable,
	Datepicker,
	DateToggle,
	ExportButton,
	PrintableReportHeader,
	Select,
	SortedColumns,
	Tooltip,
	WorkingIndicator,
} from 'components';
import { Routes as NavigationRoutes } from 'models/navigation';
import { useFilters } from 'context';
import { tabContentClassname } from 'pages/Block';
import {
	useGetLoginAnalyticsQuery,
	useGetLoginGraphQuery,
	useGetPageAnalyticsQuery,
	useGetPageGraphQuery,
	useGetUploadAnalyticsQuery,
	useGetUploadGraphQuery,
} from 'store/services/Analytics';
import { useGetAIUsageTableQuery, useGetAIUsageGraphQuery } from 'store/services/Analytics';
import { LoadingIndicator } from 'components/Select/subcomponents';
import Logo from 'assets/images/branding/logo.svg';

interface User {
	email: string;
	last_login: number; // Representing days since last login
	duration: number; // Duration on app in minutes
	logins?: number; // Optional property for logins
	visited?: number; // Optional property for pages visited
	interactions: number;
	saved_views?: number; // Optional property for saved views
	most_visited?: string; // Optional property for most visited pages
}

interface Page {
	page: string;
	visits: number; // Representing days since last login
	duration: number; // Duration on app in minutes
}

const tabTriggerClassname = classNames(
	'py-2 px-6 relative padding-x-3 text-slate-300',
	'data-active:text-sky-500 data-active:border-b-2 data-active:border-sky-500 data-active:top-[1px]'
);

// Format currency
function formatCurrency(amount: number): string {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);
}

const xAxisStyle = {
	axis: { stroke: border },
	axisLabel: { fontFamily: fontFamily, fontSize: 4, fontWeight: 600 },
	tickLabels: { fontFamily: fontFamily, fontSize: 4, padding: 7 },
};

const formatAnalyticsEndDate = (endDate: Date) => format(addDays(endDate, 0), 'M/d/yyyy');

export function Analytics() {
	const [currentTab, setCurrentTab] = useState('logins');
	const { selectedSystem } = useAppSelector((state) => state.userState);
	const { data: system } = useSystem();
	const canViewAIUsage = system?.user?.role === UserRole.super_admin;
	const healthsystem_name = system?.healthsystems.find((h) => h.id === selectedSystem)?.name;
	const [globalView, setGlobalView] = useState(selectedSystem ?? 0); // -1 if global view enabled, else contains healthsystem id
	const [printing, setPrinting] = useState(false);
	const [exportableContent, setExportableContent] = useState<any>([null]);

	// Reset globalView when selectedSystem changes to ensure synchronization
	useEffect(() => {
		setGlobalView(selectedSystem ?? 0);
	}, [selectedSystem]);

	useEffect(() => {
		if (!canViewAIUsage && currentTab === 'ai_usage') {
			setCurrentTab('logins');
		}
	}, [canViewAIUsage, currentTab]);

	// Filters
	const { dropDowns, saveDropdown } = useFilters();

	const now: Date = new Date();
	const priorDate: Date = new Date();

	priorDate.setDate(now.getDate() - 30);

	const [startDate, setStartDate] = useState(priorDate);
	const [endDate, setEndDate] = useState(now);

	const dateRangeLimit = 3;
	const [searchParams] = useSearchParams();
	const view_by_param =
		searchParams.get('view_by') ??
		(dropDowns.viewBy.value !== 'undefined' ? dropDowns.viewBy.value : undefined) ??
		'month';
	const view_by_label_pararm =
		searchParams.get('view_by_label') ??
		(dropDowns.viewBy.value !== 'undefined' ? dropDowns.viewBy.label : undefined) ??
		'Month';

	const viewBy = view_by_param;
	const viewByLabel = view_by_label_pararm;
	const appliedDateRangeKey = `${format(startDate, 'yyyy-MM-dd')}-${format(endDate, 'yyyy-MM-dd')}`;

	const generatePDF = async () => {
		// Select the div element with the id 'yourElementId'
		const element = document.getElementById('graph-container');

		if (!element) {
			return;
		}

		try {
			// Create a new jsPDF instance
			const doc = new jsPDF();

			// Options for html2canvas
			const html2canvasOptions = {
				useCORS: true, // Necessary for cross-origin images
				allowTaint: true, // Allows manipulation of the canvas
				dpi: 200, // Higher DPI for better quality
				scrollY: -window.scrollY, // Capture full content even if not visible on the screen
			};

			// Render the content in a canvas
			const canvas = await html2canvas(element, html2canvasOptions);

			// Convert the canvas to an image
			const imageData = canvas.toDataURL('image/jpeg');

			// Embed the image into the PDF
			doc.addImage(imageData, 'PNG', 10, 10, 190, 0); // Adjust these parameters as needed
			// Save the PDF
			doc.save(`${currentTab}_${globalView < 0 ? 'Merlin' : healthsystem_name}.pdf`);

			setPrinting(false);
		} catch (error) {
			console.error('Error generating PDF:', error); // keeping for easy error troubleshooting
		}
	};

	return (
		<div className='flex flex-col' id='graph-container'>
			{printing && (
				<PrintableReportHeader>
					<p className='text-[2em] font-bold leading-tight text-bluegray-900'>
						Analytics - {globalView < 0 ? 'Merlin' : healthsystem_name}
					</p>
					<p className='mt-2 text-[1.35em] font-medium text-bluegray-500'>{`${format(
						startDate,
						'M/d/yyyy'
					)} – ${formatAnalyticsEndDate(endDate)}`}</p>
				</PrintableReportHeader>
			)}
			<Tabs.Root value={currentTab} orientation='vertical'>
				<div className='flex w-full justify-between'>
					<Tabs.List aria-label='logins' className='flex border-b border-bluegray-100'>
						<Tabs.Trigger
							value='logins'
							className={tabTriggerClassname}
							onClick={() => {
								setCurrentTab('logins');
							}}
						>
							Logins
						</Tabs.Trigger>
						<Tabs.Trigger
							value='visits'
							className={tabTriggerClassname}
							onClick={() => {
								setCurrentTab('visits');
							}}
						>
							Pages Visited
						</Tabs.Trigger>
						<Tabs.Trigger
							value='uploads'
							className={tabTriggerClassname}
							onClick={() => {
								setCurrentTab('uploads');
							}}
						>
							Files Uploaded
						</Tabs.Trigger>
						{canViewAIUsage && (
							<Tabs.Trigger
								value='ai_usage'
								className={tabTriggerClassname}
								onClick={() => {
									setCurrentTab('ai_usage');
								}}
							>
								AI Usage
							</Tabs.Trigger>
						)}
					</Tabs.List>

					<div className='flex items-end gap-2 justify-end'>
						{!printing && (
							<Select
								label='View By'
								options={[
									{ label: 'Daily', value: 'daily' },
									{ label: 'Week', value: 'week' },
									{ label: 'Month', value: 'month' },
									{ label: 'Quarter', value: 'quarter' },
									{ label: 'Year', value: 'year' },
								]}
								sizeX='sm'
								onChange={(opt) => {
									if (opt) {
										dropDowns.update({ ...dropDowns, viewBy: opt });
										saveDropdown({ ...dropDowns, viewBy: opt });
									}
								}}
								value={{ label: viewByLabel, value: viewBy }}
							/>
						)}
						<div className='flex text-center bg-blue-500 items-center justify-center h-9 rounded-md cursor-pointer hover:bg-blue-600'>
							<span className='text-b3 font-semibold px-6 text-white'>
								<Datepicker
									type='range'
									shortcuts
									popoverContentProps={{ side: 'left' }}
									onApplyRange={(startDate, endDate) => {
										const normalizedStartDate = new Date(startDate);
										const normalizedEndDate = new Date(endDate);

										normalizedStartDate.setHours(0, 0, 0, 0);
										normalizedEndDate.setHours(0, 0, 0, 0);

										setStartDate(new Date(normalizedStartDate.getTime()));
										setEndDate(new Date(normalizedEndDate.getTime()));
									}}
									selectedRange={[startDate, endDate]}
									defaultEndDate={now}
									defaultStartDate={priorDate}
									dateRangeLimit={dateRangeLimit}
								>
									<div>{`${format(startDate, 'M/d/yyyy')} - ${format(endDate, 'M/d/yyyy')}`}</div>
								</Datepicker>
							</span>
						</div>
						{!printing && (
							<>
								<div className='z-30 block'>
									<ButtonStack>
										<Button
											sizeX='md'
											sizeY='md'
											variant='primary'
											className='z-30'
											onClick={() => {
												setPrinting(true);
												setTimeout(() => {
													generatePDF();
												}, 0);
											}}
										>
											Export PDF
										</Button>
										<Button sizeX='md' sizeY='md' variant='primary' className='z-30'>
											<ExportButton no_button={true} sizeX='md' sizeY='md' variant='primary' contents={exportableContent}>
												Export CSV
											</ExportButton>
										</Button>
									</ButtonStack>
								</div>
								<div>
									<Tooltip content={'Global'}>
										<Button
											sizeX='square'
											sizeY='md'
											variant={globalView < 0 ? 'primary' : 'primary-ghost'}
											onClick={() => (globalView < 0 ? setGlobalView(selectedSystem ?? 0) : setGlobalView(-1))}
										>
											<span className='material-symbols-outlined'>globe</span>
										</Button>
									</Tooltip>
								</div>
							</>
						)}
					</div>
				</div>

				<Tabs.Content value='logins' className={tabContentClassname}>
					<LoginsBarChart
						key={`logins-${globalView}-${viewBy}-${appliedDateRangeKey}`}
						stroke={'#6366f1'}
						group={viewBy}
						healthsystem_id={globalView}
						start_date={startDate}
						end_date={endDate}
					/>
				</Tabs.Content>

				<Tabs.Content value='uploads' className={tabContentClassname}>
					<UploadsBarChart
						key={`uploads-${globalView}-${viewBy}-${appliedDateRangeKey}`}
						stroke={'#10b981'}
						group={viewBy}
						healthsystem_id={globalView}
						start_date={startDate}
						end_date={endDate}
					/>
				</Tabs.Content>

				<Tabs.Content value='visits' className={tabContentClassname}>
					<VisitBarChart
						key={`visits-${globalView}-${viewBy}-${appliedDateRangeKey}`}
						stroke={'#0ea5e9'}
						group={viewBy}
						healthsystem_id={globalView}
						start_date={startDate}
						end_date={endDate}
					/>
				</Tabs.Content>

				{canViewAIUsage && (
					<Tabs.Content value='ai_usage' className={tabContentClassname}>
						<AIUsageBarChart
							key={`ai-usage-${globalView}-${viewBy}-${appliedDateRangeKey}`}
							stroke={'#8b5cf6'}
							group={viewBy}
							healthsystem_id={globalView}
							start_date={startDate}
							end_date={endDate}
						/>
					</Tabs.Content>
				)}
			</Tabs.Root>
			<div className='flex gap-4'>
				{' '}
				{currentTab === 'logins' && (
					<LoginTable
						key={`logins-table-${globalView}-${appliedDateRangeKey}`}
						setExportableData={setExportableContent}
						healthsystem_id={globalView}
						start_date={startDate}
						end_date={endDate}
					/>
				)}{' '}
				{currentTab === 'visits' && (
					<VisitTable
						key={`visits-table-${globalView}-${appliedDateRangeKey}`}
						setExportableData={setExportableContent}
						healthsystem_id={globalView}
						start_date={startDate}
						end_date={endDate}
					/>
				)}{' '}
				{currentTab === 'uploads' && (
					<UploadTable
						key={`uploads-table-${globalView}-${appliedDateRangeKey}`}
						setExportableData={setExportableContent}
						healthsystem_id={globalView}
						start_date={startDate}
						end_date={endDate}
					/>
				)}
				{canViewAIUsage && currentTab === 'ai_usage' && (
					<AIUsageTable
						key={`ai-usage-table-${globalView}-${appliedDateRangeKey}`}
						setExportableData={setExportableContent}
						healthsystem_id={globalView}
						start_date={startDate}
						end_date={endDate}
					/>
				)}
			</div>
		</div>
	);
}

interface WidgetProps {
	group: string;
	stroke: string;
	healthsystem_id: number;
	start_date: Date;
	end_date: Date;
}

interface TableProps {
	healthsystem_id: number;
	start_date: Date;
	end_date: Date;
	setExportableData: (payload: any) => void; // this format can be anything
}

export function Loader() {
	return (
		<div className='h-[40vh] w-full'>
			{' '}
			<div className='animate-pulse mt-10'>
				<img className='h-8 w-8 animate-bounce block mx-auto' src={Logo} alt='Merlin' />
			</div>
		</div>
	);
}

function LoginsBarChart({ group, stroke, healthsystem_id, start_date, end_date }: WidgetProps) {
	const { dateRange } = useFilters();
	const { data, isFetching } = useGetLoginGraphQuery({
		start_date: format(start_date, 'M/d/yyyy'),
		end_date: formatAnalyticsEndDate(end_date),
		group: group,
		healthsystem_id: healthsystem_id,
	});
	const max_y_domain = Math.max(...(data?.data_points?.map((value) => value.y) ?? [10]));
	const dynamicStyles = dynamicFontSizeStyles();
	const timeAxisStyles = dynamicStyles.timeAxisStyles;
	const isEmpty = data?.data_points && data.data_points.length > 0 ? false : true;

	return (
		<div className='max-w-screen-2xl m-auto cursor-grab relative'>
			{isFetching ? (
				<Loader />
			) : !isEmpty ? (
				<VictoryChart
					domainPadding={determineDomainPadding(data?.data_points.length ?? 0)}
					height={window.innerHeight / 6}
					minDomain={{ y: 0 }}
					maxDomain={{ y: max_y_domain * 1.3 }}
					padding={{ top: 5, bottom: 15, right: 0, left: 25 }}
					containerComponent={<VictoryZoomContainer zoomDimension={'x'} />}
				>
					{/* Y-Axis */}
					<VictoryAxis sortOrder='descending' dependentAxis padding={{ left: 0 }} style={timeAxisStyles} />
					{/* X-Axis */}
					<VictoryAxis style={xAxisStyle} tickFormat={(t) => `${t}`} />
					<VictoryBar
						style={{
							data: {
								fill: stroke,
							},
							labels: {
								fontSize: 5,
								fontFamily: 'Inter',
							},
						}}
						data={data?.data_points ?? []}
						barWidth={determineBarWidth(data?.data_points.length ?? 0) * 0.9}
						cornerRadius={2}
						labels={({ datum }) => datum.y}
					/>
				</VictoryChart>
			) : (
				<div className='flex flex-col items-center'>
					<div className='flex flex-col w-full items-center gap-6 p-8 text-p2 text-gray-600'>
						<img className='w-44' alt='An empty clipboard' src={image} />
						No data to display
					</div>
				</div>
			)}
		</div>
	);
}

function VisitBarChart({ group, stroke, healthsystem_id, start_date, end_date }: WidgetProps) {
	const { dateRange } = useFilters();
	const { data, isFetching } = useGetPageGraphQuery({
		start_date: format(start_date, 'M/d/yyyy'),
		end_date: formatAnalyticsEndDate(end_date),
		group: group,
		healthsystem_id: healthsystem_id,
	});
	const max_y_domain = Math.max(...(data?.data_points?.map((value) => value.y) ?? [10]));
	const dynamicStyles = dynamicFontSizeStyles();
	const timeAxisStyles = dynamicStyles.timeAxisStyles;
	const isEmpty = data?.data_points && data.data_points.length > 0 ? false : true;

	return (
		<div className='max-w-screen-2xl m-auto cursor-grab relative'>
			{isFetching ? (
				<Loader />
			) : !isEmpty ? (
				<VictoryChart
					domainPadding={determineDomainPadding(data?.data_points.length ?? 0)}
					height={window.innerHeight / 6}
					minDomain={{ y: 0 }}
					maxDomain={{ y: max_y_domain * 1.3 }}
					padding={{ top: 5, bottom: 15, right: 0, left: 25 }}
					containerComponent={<VictoryZoomContainer zoomDimension={'x'} />}
				>
					{/* Y-Axis */}
					<VictoryAxis sortOrder='descending' dependentAxis padding={{ left: 0 }} style={timeAxisStyles} />
					{/* X-Axis */}
					<VictoryAxis style={xAxisStyle} tickFormat={(t) => `${t}`} />
					<VictoryBar
						style={{
							data: {
								fill: stroke,
							},
							labels: {
								fontSize: 5,
								fontFamily,
							},
						}}
						data={data?.data_points ?? []}
						barWidth={determineBarWidth(data?.data_points.length ?? 0) * 0.9}
						cornerRadius={2}
						labels={({ datum }) => datum.y}
					/>
				</VictoryChart>
			) : (
				<div className='flex flex-col items-center'>
					<div className='flex flex-col w-full items-center gap-6 p-8 text-p2 text-gray-600'>
						<img className='w-44' alt='An empty clipboard' src={image} />
						No data to display
					</div>
				</div>
			)}
		</div>
	);
}

function UploadsBarChart({ group, stroke, healthsystem_id, start_date, end_date }: WidgetProps) {
	const { dateRange } = useFilters();
	const { data, isFetching } = useGetUploadGraphQuery({
		start_date: format(start_date, 'M/d/yyyy'),
		end_date: formatAnalyticsEndDate(end_date),
		group: group,
		healthsystem_id: healthsystem_id,
	});
	const max_y_domain = Math.max(...(data?.data_points?.map((value) => value.y) ?? [10]));
	const dynamicStyles = dynamicFontSizeStyles();
	const timeAxisStyles = dynamicStyles.timeAxisStyles;
	const isEmpty = data?.data_points && data.data_points.length > 0 ? false : true;

	return (
		<div className='max-w-screen-2xl m-auto overflow-x-auto cursor-grab relative'>
			{isFetching ? (
				<Loader />
			) : !isEmpty ? (
				<VictoryChart
					domainPadding={determineDomainPadding(data?.data_points.length ?? 0)}
					height={window.innerHeight / 6}
					minDomain={{ y: 0 }}
					maxDomain={{ y: max_y_domain * 1.3 }}
					padding={{ top: 5, bottom: 15, right: 0, left: 25 }}
					containerComponent={<VictoryZoomContainer zoomDimension={'x'} />}
				>
					{/* Y-Axis */}
					<VictoryAxis
						tickFormat={(t) => `${t}`}
						sortOrder='descending'
						dependentAxis
						padding={{ left: 0 }}
						style={timeAxisStyles}
					/>
					{/* X-Axis */}
					<VictoryAxis style={xAxisStyle} tickFormat={(t) => `${t}`} />
					<VictoryBar
						style={{
							data: {
								fill: stroke,
							},
							labels: {
								fontSize: 5,
								fontFamily,
							},
						}}
						data={data?.data_points ?? []}
						barWidth={determineBarWidth(data?.data_points.length ?? 3)}
						cornerRadius={1}
						labels={({ datum }) => datum.y}
					/>
				</VictoryChart>
			) : (
				<div className='flex flex-col items-center'>
					<div className='flex flex-col w-full items-center gap-6 p-8 text-p2 text-gray-600'>
						<img className='w-44' alt='An empty clipboard' src={image} />
						No data to display
					</div>
				</div>
			)}
		</div>
	);
}

function LoginTable({ healthsystem_id, start_date, end_date, setExportableData }: TableProps) {
	const [sort, setSort] = useState<SortedColumns[]>([]);
	const { dateRange } = useFilters();
	const { data, isFetching } = useGetLoginAnalyticsQuery({
		start_date: format(start_date, 'M/d/yyyy'),
		end_date: formatAnalyticsEndDate(end_date),
		sort: sort,
		healthsystem_id: healthsystem_id,
	});

	useEffect(() => {
		setExportableData(data?.stats ?? [null]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	return (
		<div className='w-full flex flex-col'>
			<div className='flex justify-between items-baseline bg-gray-50 mt-4 p-1 pb-2.5 rounded-md px-3'>
				<div className='font-semibold'>User Breakdown</div>
				<div>
					{isFetching ? (
						<div className='text-blue-500 w-12 flex justify-center h-4 align-middle items-center pt-4'>
							<LoadingIndicator />
						</div>
					) : (
						<div className='text-blue-500 opacity-0 w-12 flex justify-center h-4 align-middle items-center pt-4'>
							<LoadingIndicator />
						</div>
					)}
				</div>
			</div>
			<DataTable
				minimalStyle
				disableRowCounter
				disablePagination
				onSort={(arr) => {
					setSort(arr);
				}}
				columns={[
					{
						accessorKey: 'name',
						header: 'Name',
						enableGlobalFilter: false,
						enableSorting: false,
					},
					{
						accessorKey: 'email',
						header: 'User',
						enableGlobalFilter: false,
						enableSorting: false,
					},
					{
						accessorKey: 'healthsystem_name',
						header: 'System',
						enableGlobalFilter: false,
						enableSorting: false,
					},
					{
						accessorKey: 'days_since_last_login',
						header: 'Last Login',
						enableGlobalFilter: false,
						cell: ({ row }) => {
							return `${row.original.days_since_last_login} days`;
						},
					},
					{
						accessorKey: 'number_of_unique_days',
						header: 'Logins',
						enableGlobalFilter: false,
					},
					{
						accessorKey: 'interactions',
						header: 'Interactions',
						enableGlobalFilter: false,
					},
					{
						accessorKey: 'session_time',
						header: 'Session Duration',
						enableGlobalFilter: false,
						cell: ({ row }) => {
							return `${row.original.session_time}s`;
						},
					},
				]}
				data={data?.stats ?? []}
				title={''}
			/>
		</div>
	);
}

function UploadTable({ healthsystem_id, start_date, end_date, setExportableData }: TableProps) {
	const [sort, setSort] = useState<SortedColumns[]>([]);
	const { dateRange } = useFilters();
	const { data, isFetching } = useGetUploadAnalyticsQuery({
		start_date: format(start_date, 'M/d/yyyy'),
		end_date: formatAnalyticsEndDate(end_date),
		sort: sort,
		healthsystem_id: healthsystem_id,
	});

	useEffect(() => {
		setExportableData(data?.stats ?? [null]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	return (
		<div className='w-full flex flex-col'>
			<div className='flex justify-between items-baseline bg-gray-50 mt-4 p-1 pb-2.5 rounded-md px-3'>
				<div className='font-semibold'>Upload Receipts</div>
				<div>
					{isFetching ? (
						<div className='text-blue-500 w-12 flex justify-center h-4 align-middle items-center pt-4'>
							<LoadingIndicator />
						</div>
					) : (
						<div className='text-blue-500 opacity-0 w-12 flex justify-center h-4 align-middle items-center pt-4'>
							<LoadingIndicator />
						</div>
					)}
				</div>
			</div>
			<DataTable
				minimalStyle
				disableRowCounter
				disablePagination
				onSort={(arr) => {
					setSort(arr);
				}}
				columns={[
					{
						accessorKey: 'container_name',
						header: 'Container',
						enableGlobalFilter: false,
						enableSorting: false,
					},
					{
						accessorKey: 'uploaded_at',
						header: 'Uploaded At',
						enableGlobalFilter: false,
						enableSorting: false,
					},
					{
						accessorKey: 'filename',
						header: 'Filename',
						enableGlobalFilter: false,
						enableSorting: false,
					},
					{
						accessorKey: 'file_type',
						header: 'Type',
						enableGlobalFilter: false,
						enableSorting: false,
					},
				]}
				data={data?.stats ?? []}
				title={''}
			/>
		</div>
	);
}

function VisitTable({ healthsystem_id, start_date, end_date, setExportableData }: TableProps) {
	const [sort, setSort] = useState<SortedColumns[]>([]);
	const { dateRange } = useFilters();
	const { data, isFetching } = useGetPageAnalyticsQuery({
		start_date: format(start_date, 'M/d/yyyy'),
		end_date: formatAnalyticsEndDate(end_date),
		sort: sort,
		healthsystem_id: healthsystem_id,
	});

	useEffect(() => {
		setExportableData(data?.stats ?? [null]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);
	return (
		<div className='w-full flex flex-col'>
			<div className='flex justify-between items-baseline bg-gray-50 mt-4 p-1 pb-2.5 rounded-md px-3'>
				<div className='font-semibold'>Page Breakdown</div>
				<div>
					{isFetching ? (
						<div className='text-blue-500 w-12 flex justify-center h-4 align-middle items-center pt-4'>
							<LoadingIndicator />
						</div>
					) : (
						<div className='text-blue-500 opacity-0 w-12 flex justify-center h-4 align-middle items-center pt-4'>
							<LoadingIndicator />
						</div>
					)}
				</div>
			</div>
			<DataTable
				minimalStyle
				disableRowCounter
				disablePagination
				onSort={(arr) => {
					setSort(arr);
				}}
				columns={[
					{
						accessorKey: 'page_url',
						header: 'Page',
						enableGlobalFilter: false,
						enableSorting: false,
					},
					{
						accessorKey: 'page_url',
						header: 'Module',
						enableGlobalFilter: false,
						enableSorting: false,
						cell: ({ row }) => <p>{row.original.page_url.split('/').at(1)}</p>,
					},
					{
						accessorKey: 'visits',
						header: 'Visits',
						enableGlobalFilter: false,
					},
					{
						accessorKey: 'interactions',
						header: 'Interactions',
						enableGlobalFilter: false,
					},
					{
						accessorKey: 'session_time',
						header: 'Session Duration',
						enableGlobalFilter: false,
					},
				]}
				data={data?.stats ?? []}
				title={''}
			/>
		</div>
	);
}

function AIUsageBarChart({ group, stroke, healthsystem_id, start_date, end_date }: WidgetProps) {
	const { dateRange } = useFilters();

	const { data, isFetching } = useGetAIUsageGraphQuery({
		start_date: format(start_date, 'M/d/yyyy'), // format(dateRange.applied.startDate, 'M/d/yyyy'),
		end_date: formatAnalyticsEndDate(end_date), // formatAnalyticsEndDate(dateRange.applied.endDate),
		group: group,
		healthsystem_id: healthsystem_id,
	});

	// Transform data points from y_in/y_out format to use combined y value for the chart
	const transformedDataPoints =
		data?.data_points?.map((point) => ({
			x: point.x,
			y: (point.y_in ?? 0) + (point.y_out ?? 0),
			input_tokens: point.y_in,
			output_tokens: point.y_out,
		})) ?? [];

	const max_y_domain = Math.max(...(transformedDataPoints.map((value) => value.y) ?? [10]));
	const yAxisLeftPadding = max_y_domain >= 1_000_000 ? 58 : 35;
	const dynamicStyles = dynamicFontSizeStyles();
	const timeAxisStyles = dynamicStyles.timeAxisStyles;
	const isEmpty = data?.data_points && data.data_points.length > 0 ? false : true;

	return (
		<div className='max-w-screen-2xl m-auto cursor-grab relative'>
			{isFetching ? (
				<Loader />
			) : !isEmpty ? (
				<VictoryChart
					domainPadding={determineDomainPadding(transformedDataPoints.length ?? 0)}
					height={window.innerHeight / 6}
					minDomain={{ y: 0 }}
					maxDomain={{ y: max_y_domain * 1.3 }}
					padding={{ top: 5, bottom: 15, right: 0, left: yAxisLeftPadding }}
					containerComponent={<VictoryZoomContainer zoomDimension={'x'} />}
				>
					{/* Y-Axis */}
					<VictoryAxis
						sortOrder='descending'
						dependentAxis
						padding={{ left: 0 }}
						tickFormat={(tick) => Number(tick).toLocaleString()}
						style={timeAxisStyles}
					/>
					{/* X-Axis */}
					<VictoryAxis style={xAxisStyle} tickFormat={(t) => `${t}`} />
					<VictoryBar
						style={{
							data: {
								fill: stroke,
							},
							labels: {
								fontSize: 5,
								fontFamily: 'Inter',
							},
						}}
						data={transformedDataPoints}
						barWidth={determineBarWidth(transformedDataPoints.length ?? 0) * 0.9}
						cornerRadius={2}
						labels={({ datum }) => (datum?.y != null ? datum.y.toLocaleString() : '0')}
					/>
				</VictoryChart>
			) : (
				<div className='flex flex-col items-center'>
					<div className='flex flex-col w-full items-center gap-6 p-8 text-p2 text-gray-600'>
						<img className='w-44' alt='An empty clipboard' src={image} />
						No data to display
					</div>
				</div>
			)}
		</div>
	);
}

function AIUsageTable({ healthsystem_id, start_date, end_date, setExportableData }: TableProps) {
	const [sort, setSort] = useState<SortedColumns[]>([]);
	const { dateRange } = useFilters();
	const { data, isFetching } = useGetAIUsageTableQuery({
		start_date: format(start_date, 'M/d/yyyy'),
		end_date: formatAnalyticsEndDate(end_date),
		sort: sort,
		healthsystem_id: healthsystem_id,
	});

	useEffect(() => {
		setExportableData(data?.stats ?? [null]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	const formatNumber = (num: number | undefined) => {
		if (num === undefined || num === null) return '0';
		return num.toLocaleString();
	};

	const formatTokens = (num: number | undefined) => {
		if (num === undefined || num === null) return '0';
		return num.toLocaleString();
	};

	const totalBillable = data?.total_billable ?? 0;

	const usageSummaryCards = [
		{
			title: 'Total Requests',
			value: formatNumber(data?.total_requests),
			className: 'bg-purple-50 border-purple-200 text-purple-900',
			titleClassName: 'text-purple-700',
		},
		{
			title: 'Input Tokens',
			value: formatTokens(data?.total_input_tokens),
			className: 'bg-indigo-50 border-indigo-200 text-indigo-900',
			titleClassName: 'text-indigo-700',
		},
		{
			title: 'Output Tokens',
			value: formatTokens(data?.total_output_tokens),
			className: 'bg-blue-50 border-blue-200 text-blue-900',
			titleClassName: 'text-blue-700',
		},
		{
			title: 'Cache Read Tokens',
			value: formatTokens(data?.total_cache_read_tokens),
			className: 'bg-cyan-50 border-cyan-200 text-cyan-900',
			titleClassName: 'text-cyan-700',
		},
		{
			title: 'Resolver Calls',
			value: formatNumber(data?.total_resolver_calls),
			className: 'bg-pink-50 border-pink-200 text-pink-900',
			titleClassName: 'text-pink-700',
		},
		{
			title: 'Billable Cost',
			value: formatCurrency(totalBillable),
			className: 'bg-emerald-50 border-emerald-200 text-emerald-900',
			titleClassName: 'text-emerald-700',
		},
	];

	return (
		<div className='w-full flex flex-col'>
			<div className='rounded-lg border border-bluegray-100 bg-white p-4 mt-4'>
				<div className='flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between'>
					<p className='text-base font-semibold text-gray-900'>Usage Overview</p>
					<p className='text-xs text-gray-500'>All values reflect the current filter range</p>
				</div>

				<div className='grid gap-3 mt-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6'>
					{usageSummaryCards.map((card) => (
						<div key={card.title} className={classNames('rounded-lg border p-3', card.className)}>
							<div className={classNames('text-xs font-semibold uppercase tracking-wide', card.titleClassName)}>
								{card.title}
							</div>
							<div className='text-xl font-bold mt-2 truncate'>{card.value}</div>
						</div>
					))}
				</div>

				<div className='grid gap-3 mt-3 grid-cols-1 md:grid-cols-3'>
					<div className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
						<div className='text-[11px] uppercase tracking-wide text-gray-500 font-semibold'>Graph Input Tokens</div>
						<div className='text-base font-semibold text-gray-900 mt-1'>{formatTokens(data?.total_graph_input_tokens)}</div>
					</div>
					<div className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
						<div className='text-[11px] uppercase tracking-wide text-gray-500 font-semibold'>Graph Output Tokens</div>
						<div className='text-base font-semibold text-gray-900 mt-1'>{formatTokens(data?.total_graph_output_tokens)}</div>
					</div>
					<div className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
						<div className='text-[11px] uppercase tracking-wide text-gray-500 font-semibold'>Cache Creation Tokens</div>
						<div className='text-base font-semibold text-gray-900 mt-1'>
							{formatTokens(data?.total_cache_creation_tokens)}
						</div>
					</div>
				</div>
			</div>

			<div className='flex justify-between items-baseline bg-gray-50 mt-4 p-1 pb-2.5 rounded-md px-3'>
				<div className='font-semibold'>Token Usage Breakdown</div>
				<div>
					{isFetching ? (
						<div className='text-blue-500 w-12 flex justify-center h-4 align-middle items-center pt-4'>
							<LoadingIndicator />
						</div>
					) : (
						<div className='text-blue-500 opacity-0 w-12 flex justify-center h-4 align-middle items-center pt-4'>
							<LoadingIndicator />
						</div>
					)}
				</div>
			</div>
			<DataTable
				minimalStyle
				disableRowCounter
				disablePagination
				onSort={(arr) => {
					setSort(arr);
				}}
				columns={[
					{
						accessorKey: 'user',
						header: 'User',
						enableGlobalFilter: false,
						enableSorting: true,
					},
					{
						accessorKey: 'model_name',
						header: 'Model',
						enableGlobalFilter: false,
						enableSorting: true,
						cell: ({ row }) => (
							<div className='text-xs leading-5'>
								<div className='font-medium text-gray-900'>{row.original.model_name ?? 'Unknown'}</div>
								<div className='text-gray-500'>{row.original.deployment_name ?? 'No deployment mapped'}</div>
							</div>
						),
					},
					{
						accessorKey: 'request_count',
						header: 'Requests',
						enableGlobalFilter: false,
						enableSorting: true,
					},
					{
						accessorKey: 'total_tokens',
						header: 'Core Token Usage',
						enableGlobalFilter: false,
						cell: ({ row }) => (
							<div className='text-xs leading-5'>
								<div>
									<span className='text-gray-500'>Input:</span>{' '}
									<span className='font-medium text-gray-800'>{formatTokens(row.original.input_tokens)}</span>
								</div>
								<div>
									<span className='text-gray-500'>Cached Input:</span>{' '}
									<span className='font-medium text-gray-800'>
										{formatTokens(row.original.cached_input_tokens ?? row.original.cache_read_tokens)}
									</span>
								</div>
								<div>
									<span className='text-gray-500'>Output:</span>{' '}
									<span className='font-medium text-gray-800'>{formatTokens(row.original.output_tokens)}</span>
								</div>
								<div className='pt-0.5 border-t border-gray-100 mt-1'>
									<span className='text-gray-600'>Total:</span>{' '}
									<span className='font-semibold text-gray-900'>{formatTokens(row.original.total_tokens)}</span>
								</div>
							</div>
						),
					},
					{
						accessorKey: 'graph_input_tokens',
						header: 'Graph Tokens',
						enableGlobalFilter: false,
						cell: ({ row }) => (
							<div className='text-xs leading-5'>
								<div>
									<span className='text-gray-500'>Input:</span>{' '}
									<span className='font-medium text-gray-800'>{formatTokens(row.original.graph_input_tokens)}</span>
								</div>
								<div>
									<span className='text-gray-500'>Output:</span>{' '}
									<span className='font-medium text-gray-800'>{formatTokens(row.original.graph_output_tokens)}</span>
								</div>
							</div>
						),
					},
					{
						accessorKey: 'resolver_calls',
						header: 'Resolver Activity',
						enableGlobalFilter: false,
						cell: ({ row }) => (
							<div className='text-xs leading-5'>
								<div>
									<span className='text-gray-500'>Calls:</span>{' '}
									<span className='font-medium text-gray-800'>{formatNumber(row.original.resolver_calls)}</span>
								</div>
								<div>
									<span className='text-gray-500'>Input:</span>{' '}
									<span className='font-medium text-gray-800'>{formatTokens(row.original.resolver_input_tokens)}</span>
								</div>
								<div>
									<span className='text-gray-500'>Output:</span>{' '}
									<span className='font-medium text-gray-800'>{formatTokens(row.original.resolver_output_tokens)}</span>
								</div>
							</div>
						),
					},
					{
						accessorKey: 'billable',
						header: 'Cost Breakdown',
						enableGlobalFilter: false,
						enableSorting: true,
						cell: ({ row }) => (
							<div className='text-xs leading-5'>
								<div>
									<span className='text-gray-500'>Input:</span>{' '}
									<span className='font-medium text-gray-800'>{formatCurrency(row.original.input_cost ?? 0)}</span>
								</div>
								<div>
									<span className='text-gray-500'>Cached:</span>{' '}
									<span className='font-medium text-gray-800'>{formatCurrency(row.original.cached_input_cost ?? 0)}</span>
								</div>
								<div>
									<span className='text-gray-500'>Cache writes:</span>{' '}
									<span className='font-medium text-gray-800'>{formatCurrency(row.original.cache_write_cost ?? 0)}</span>
								</div>
								<div>
									<span className='text-gray-500'>Output:</span>{' '}
									<span className='font-medium text-gray-800'>{formatCurrency(row.original.output_cost ?? 0)}</span>
								</div>
								<div className='pt-0.5 border-t border-gray-100 mt-1'>
									<span className='text-gray-600'>Billable:</span>{' '}
									<span className='font-semibold text-gray-900'>{formatCurrency(row.original.billable ?? 0)}</span>
								</div>
							</div>
						),
					},
				]}
				data={data?.stats ?? []}
				title={''}
			/>
		</div>
	);
}

export default Analytics;
